import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './KnowledgePanelInfo.module.css';

export type KnowledgePanelInfoKey = 'ops' | 'evaluation' | 'settings';

type PanelInfoTerm = { term: string; detail: string };

const asStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const asTermList = (value: unknown): PanelInfoTerm[] =>
  Array.isArray(value)
    ? value.filter((item): item is PanelInfoTerm =>
      Boolean(item) && typeof item === 'object'
      && typeof (item as PanelInfoTerm).term === 'string'
      && typeof (item as PanelInfoTerm).detail === 'string')
    : [];

export interface KnowledgePanelInfoProps {
  /** Which Operations snapshot panel this icon explains; content lives under `panelInfo.<panel>` in the knowledge namespace. */
  panel: KnowledgePanelInfoKey;
  /** Panel title, used for the accessible label and the popover heading. */
  panelTitle: string;
}

/**
 * Round "i" button rendered next to a panel title. Clicking it opens a popover that explains what the
 * panel does, how the numbers are produced and how to read each item. Closes on Escape or outside click.
 */
export const KnowledgePanelInfo: React.FC<KnowledgePanelInfoProps> = ({ panel, panelTitle }) => {
  const { t } = useTranslation('knowledge');
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLSpanElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const dialogId = React.useId();

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const what = t(`panelInfo.${panel}.what`, { defaultValue: '' });
  const how = asStringList(t(`panelInfo.${panel}.how`, { returnObjects: true, defaultValue: [] }));
  const reading = asTermList(t(`panelInfo.${panel}.reading`, { returnObjects: true, defaultValue: [] }));
  const notes = asStringList(t(`panelInfo.${panel}.notes`, { returnObjects: true, defaultValue: [] }));
  const ariaLabel = t('panelInfo.ariaLabel', { panel: panelTitle, defaultValue: `About ${panelTitle}` });

  return (
    <span ref={wrapperRef} className={styles.wrapper}>
      <button
        ref={buttonRef}
        type="button"
        className={`${styles.icon} ${open ? styles.iconActive : ''}`}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={dialogId}
        onClick={() => setOpen(current => !current)}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="8" cy="4.9" r="0.95" fill="currentColor" />
          <path d="M6.9 7.2h1.6v4.3H6.9z" fill="currentColor" />
        </svg>
      </button>

      {open ? (
        <div id={dialogId} role="dialog" aria-label={ariaLabel} className={styles.popover}>
          <div className={styles.popoverHeader}>
            <span className={styles.popoverTitle}>{panelTitle}</span>
            <button
              type="button"
              className={styles.closeButton}
              aria-label={t('panelInfo.close', { defaultValue: 'Close' })}
              onClick={() => { setOpen(false); buttonRef.current?.focus(); }}
            >
              ×
            </button>
          </div>

          <div className={styles.popoverBody}>
            {what ? (
              <section className={styles.section}>
                <h5 className={styles.sectionHeading}>{t('panelInfo.whatHeading', { defaultValue: 'What it does' })}</h5>
                <p className={styles.paragraph}>{what}</p>
              </section>
            ) : null}

            {how.length > 0 ? (
              <section className={styles.section}>
                <h5 className={styles.sectionHeading}>{t('panelInfo.howHeading', { defaultValue: 'How it works' })}</h5>
                <ol className={styles.steps}>
                  {how.map((step, index) => <li key={index}>{step}</li>)}
                </ol>
              </section>
            ) : null}

            {reading.length > 0 ? (
              <section className={styles.section}>
                <h5 className={styles.sectionHeading}>{t('panelInfo.readingHeading', { defaultValue: 'How to read it' })}</h5>
                <dl className={styles.terms}>
                  {reading.map(item => (
                    <div key={item.term} className={styles.termRow}>
                      <dt className={styles.term}>{item.term}</dt>
                      <dd className={styles.termDetail}>{item.detail}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}

            {notes.length > 0 ? (
              <section className={styles.section}>
                <h5 className={styles.sectionHeading}>{t('panelInfo.notesHeading', { defaultValue: 'Good to know' })}</h5>
                <ul className={styles.notes}>
                  {notes.map((note, index) => <li key={index}>{note}</li>)}
                </ul>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </span>
  );
};

export default KnowledgePanelInfo;
